import { type ParsedUrlQuery } from 'node:querystring'
import { isOnServer } from '@shared/constants/util'
import { parse, parseUrl, stringify } from '@/features/shared/utils/queryString'
import { SKIP_SSR } from '@shared/constants/routes'

/**
 * clean url of specific param(s). Allows for passing of 1 to n number of params
 */
export const cleanUrlOfParams = (param: string, ...params: string[]) => {
  if (isOnServer()) return
  const url = new URL(window.location.href)
  const paramsToRemove = [param, ...params]

  for (const key of paramsToRemove) {
    url.searchParams.delete(key)
  }

  window.history.replaceState({}, '', url.toString())
}

export const cleanUrlOfRedirectParams = () => {
  if (isOnServer()) return
  const url = new URL(window.location.href)
  const redirectParams = [
    'redirect_count',
    'redirect_from',
    'redirect_url',
    'redirect_from_invalid_url', // more explicit key here, stating that these should be cause for concern?
    SKIP_SSR,
  ]
  const containsRedirectKeys = [...url.searchParams.keys()].some((key) =>
    redirectParams.includes(key),
  )
  if (!containsRedirectKeys) {
    return
  }

  for (const param of redirectParams) {
    url.searchParams.delete(param)
  }
  return url.pathname + url.search
}

export const getSearchQueryString = (query: ParsedUrlQuery | URLSearchParams) => {
  if (query instanceof URLSearchParams) {
    const queryString = query.toString()
    return queryString ? `?${queryString}` : ''
  }
  if (typeof query !== 'object' || Object.keys(query).length === 0) {
    return ''
  }
  return `?${stringify(query)}`
}

export const getUtmParams = (addReferrerUrl = true) => {
  const params = parse(window.location.search)
  const utmReferrer = document.referrer.includes('utm_') ? document.referrer : ''
  const utmParams = parseUrl(utmReferrer).query
  return {
    ...utmParams,
    ...params,
    ...(addReferrerUrl && {
      referrerUrl: window.location.href,
    }),
  }
}

interface NextUrlOptions {
  queryParam?: string
}
export const getUrlWithNextParam = (
  destination: string,
  nextUrl?: string,
  { queryParam = 'next' }: NextUrlOptions = {},
) => {
  if (!nextUrl || nextUrl.includes('/_next')) return destination
  return `${destination}?${queryParam}=${encodeURIComponent(nextUrl)}`
}

/**
 * Returns true if the URL is a route controlled by Segway.
 * Note that tampering with this logic may cause CodeQL errors.
 * https://codeql.github.com/codeql-query-help/javascript/js-server-side-unvalidated-url-redirection/#example
 */
export function isSafeSegwayURL(url: string): boolean {
  if (url === '') return false
  const trustedOrigins = new Set(['https://www.shipt.com', 'https://shipt.com'])

  try {
    const origin = new URL(url, 'https://www.shipt.com').origin
    return trustedOrigins.has(origin)
  } catch (e) {
    return false
  }
}

export const isSamePathAndQuery = (href: string): boolean => {
  if (isOnServer()) return false

  const current = window.location
  const target = new URL(href, window.location.href)
  return current.pathname === target.pathname && current.search === target.search
}
